'use client'
import React from 'react'
import cx from 'classnames'
import { Message } from '@locmod/intl'
import { usePathname } from 'next/navigation'
import { constants } from 'helpers'
import { Href } from 'components/navigation'
import { Icon, type IconName } from 'components/ui'

import { useIsMounted } from 'hooks'
import messages from './messages'
import { useUserContext } from '@/contexts/user'

interface MenuItem {
  icon: IconName // this makes sure only valid icon names are allowed
  title: Intl.Message
  link: string
  isExternal: boolean
}

const staticMenu: MenuItem[] = [
  {
    icon: 'interface/user_avatar_secondary',
    title: messages.profile,
    link: constants.navigationLinks.profile,
    isExternal: false,
  },
  {
    icon: 'interface/referral',
    title: messages.affiliate,
    link: constants.navigationLinks.affiliate,
    isExternal: true,
  },
  {
    icon: 'interface/promotion',
    title: messages.promotion,
    link: constants.navigationLinks.promotions,
    isExternal: true,
  },
  {
    icon: 'interface/mybets',
    title: messages.whitepaper,
    link: constants.navigationLinks.whitepaper,
    isExternal: true,
  },
  {
    icon: 'interface/blog',
    title: messages.blog,
    link: constants.navigationLinks.blog,
    isExternal: true,
  },
  {
    icon: 'interface/info-circle',
    title: messages.about,
    link: constants.navigationLinks.about,
    isExternal: true,
  },
]

const StaticNavigation = () => {
  const pathname = usePathname()
  const { isLoginComplete } = useUserContext()
  const isMounted = useIsMounted()

  const filteredMenu = staticMenu.filter(item =>
    item.link === constants.navigationLinks.profile
      ? isMounted() && isLoginComplete // Only show after mount and if logged in
      : true
  )

  return (
    <div className="flex flex-col sticky bottom-0 py-1 mx-2 bg-bg-l0">
      {filteredMenu.map(({ title, icon, link, isExternal }, index) => (
        <Href
          key={`${title}-${index}`}
          to={link}
          target={isExternal ? '_blank' : '_self'}
          className={cx(
            'group flex items-center justify-between px-4 cursor-pointer ',
            {
              'bg-card-border-top rounded-3xl border border-b-0 border-secondary/40':
                pathname === link,
            }
          )}
        >
          <span className="flex gap-2 items-center">
            <span
              className={cx(
                'flex items-center group-hover:text-brand-50 w-4 h-4',
                { 'text-brand-50': pathname === link }
              )}
            >
              <Icon className="w-full" name={icon} />
            </span>
            <Message
              className={cx(
                'text-caption-14 font-semibold py-2 group-hover:text-brand-50',
                { 'text-brand-50': pathname === link }
              )}
              value={title}
            />
          </span>
          <span
            className={cx(
              'flex items-center text-secondary w-4 h-4 group-hover:text-brand-50 ',
              { 'text-brand-50 !stroke-brand-50': pathname === link },
              {
                'stroke-secondary group-hover:stroke-brand-50':
                  link === '/profile',
              }
            )}
          >
            <Icon
              name={
                isExternal
                  ? 'interface/external_link'
                  : 'interface/chevron_right_secondary'
              }
            />
          </span>
        </Href>
      ))}
    </div>
  )
}

export default StaticNavigation
